// @ts-nocheck
/**
 * MaterialDetailScreen - 素材詳細画面
 *
 * 素材の入手方法と用途を自動逆引きして表示
 * ドロップテーブル・レシピを動的にスキャン（ハードコードなし）
 */

import React, { useMemo } from 'react';
import { useCardDex } from '../../hooks/useCardDex';
import { useCardData } from '../CardDexScreen';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import {
  ATTRIBUTE_MATERIALS,
  CATEGORY_MATERIALS,
  GENERIC_MATERIALS,
  EXP_ORB_MATERIALS,
  RARITY_FRAGMENTS,
} from '../../../../twinKey/content/alchemy/data/materialTypes';
import {
  FLOOR_DROP_TABLES,
  CATEGORY_DROP_TABLES,
  BOSS_DROP_TABLES,
  CARD_DROP_TABLES,
  CHEST_DROP_TABLES,
} from '../../../../twinKey/content/drops/dropTables';
import { RECIPES } from '../../../../twinKey/content/alchemy/data/recipes';
import { COLORS, navStyles } from '../../styles/cardDexStyles';

// ========================================
// ヘルパー関数
// ========================================

/**
 * IDから素材情報を取得
 */
function getMaterialById(materialId) {
  const allMaterials = {
    ...ATTRIBUTE_MATERIALS,
    ...CATEGORY_MATERIALS,
    ...GENERIC_MATERIALS,
    ...EXP_ORB_MATERIALS,
    ...RARITY_FRAGMENTS,
  };
  return allMaterials[materialId] || null;
}

/**
 * 入手方法を自動逆引き
 */
function getObtainMethods(materialName) {
  const methods = {
    floors: [],      // 大陸ドロップ
    categories: [],  // カテゴリドロップ
    bosses: [],      // ボスドロップ
    cards: [],       // カード固有ドロップ
    chests: [],      // 宝箱ドロップ
  };

  if (!materialName) return methods;

  // FLOOR_DROP_TABLES 逆引き
  for (const [tableKey, table] of Object.entries(FLOOR_DROP_TABLES)) {
    // 通常ドロップ
    if (table.materials) {
      const normalDrop = table.materials.find((m) => m.item === materialName);
      if (normalDrop) {
        methods.floors.push({
          location: tableKey,
          chance: normalDrop.chance,
          countRange: normalDrop.countRange,
          type: 'normal',
        });
      }
    }
    // レアドロップ
    if (table.rarePool) {
      const rareDrop = table.rarePool.find((r) => r.item === materialName);
      if (rareDrop) {
        methods.floors.push({
          location: tableKey,
          chance: table.rareChance,
          weight: rareDrop.weight,
          type: 'rare',
        });
      }
    }
  }

  // CATEGORY_DROP_TABLES 逆引き
  for (const [category, table] of Object.entries(CATEGORY_DROP_TABLES)) {
    if (table.materials) {
      const drop = table.materials.find((m) => m.item === materialName);
      if (drop) {
        methods.categories.push({
          category,
          chance: drop.chance,
          countRange: drop.countRange,
        });
      }
    }
    if (table.rarePool) {
      const rareDrop = table.rarePool.find((r) => r.item === materialName);
      if (rareDrop) {
        methods.categories.push({
          category,
          chance: table.rareChance,
          weight: rareDrop.weight,
          type: 'rare',
        });
      }
    }
  }

  // BOSS_DROP_TABLES 逆引き
  for (const [bossId, table] of Object.entries(BOSS_DROP_TABLES)) {
    // 確定ドロップ
    if (table.guaranteed) {
      const guaranteed = table.guaranteed.find((g) => g.item === materialName);
      if (guaranteed) {
        methods.bosses.push({
          bossId,
          count: guaranteed.count,
          type: 'guaranteed',
        });
      }
    }
    // レアドロップ
    if (table.rareDrops) {
      const rare = table.rareDrops.find((r) => r.item === materialName);
      if (rare) {
        methods.bosses.push({
          bossId,
          chance: rare.chance,
          count: rare.count,
          type: 'rare',
        });
      }
    }
    // 初回クリアボーナス
    if (table.firstClearBonus) {
      const firstClear = table.firstClearBonus.find((f) => f.item === materialName);
      if (firstClear) {
        methods.bosses.push({
          bossId,
          count: firstClear.count,
          type: 'firstClear',
        });
      }
    }
  }

  // CARD_DROP_TABLES 逆引き
  for (const [cardId, table] of Object.entries(CARD_DROP_TABLES)) {
    if (table.guaranteed) {
      const guaranteed = table.guaranteed.find((g) => g.item === materialName);
      if (guaranteed) {
        methods.cards.push({ cardId, count: guaranteed.count, type: 'guaranteed' });
      }
    }
    if (table.materials) {
      const normal = table.materials.find((m) => m.item === materialName);
      if (normal) {
        methods.cards.push({
          cardId,
          chance: normal.chance,
          countRange: normal.countRange,
          type: 'normal',
        });
      }
    }
    if (table.rarePool) {
      const rare = table.rarePool.find((r) => r.item === materialName);
      if (rare) {
        methods.cards.push({
          cardId,
          weight: rare.weight,
          type: 'rare',
        });
      }
    }
  }

  // CHEST_DROP_TABLES 逆引き
  for (const [chestType, table] of Object.entries(CHEST_DROP_TABLES)) {
    if (table.items) {
      const drop = table.items.find((i) => i.item === materialName);
      if (drop) {
        methods.chests.push({
          chestType,
          weight: drop.weight,
          countRange: drop.countRange,
        });
      }
    }
  }

  return methods;
}

/**
 * 用途を自動逆引き
 */
function getUsages(materialName) {
  const usages = [];

  if (!materialName) return usages;

  for (const [recipeId, recipe] of Object.entries(RECIPES)) {
    if (recipe.materials) {
      const requirement = recipe.materials.find((m) => m.type === materialName);
      if (requirement) {
        usages.push({
          recipeId,
          recipeName: recipe.name,
          recipeType: recipe.type,
          recipeTier: recipe.tier,
          requiredCount: requirement.count,
        });
      }
    }
  }

  return usages;
}

/**
 * ロケーション名を日本語に変換
 * FLOOR_DROP_TABLES と CONTINENTS の定義に準拠
 */
function formatLocation(locationKey) {
  const locationMap = {
    // 巨鳥の島アヴィクルス（炎・光）
    aviculus_depth_1: '巨鳥の島アヴィクルス 深度1',
    aviculus_depth_2: '巨鳥の島アヴィクルス 深度2',
    aviculus_depth_3: '巨鳥の島アヴィクルス 深度3',
    // 黒呪の大陸（闇・水）
    black_curse_depth_1: '黒呪の大陸 深度1',
    black_curse_depth_2: '黒呪の大陸 深度2',
    black_curse_depth_3: '黒呪の大陸 深度3',
    // 蒸気の境界（炎・水）
    steam_boundary_depth_1: '蒸気の境界 深度1',
    steam_boundary_depth_2: '蒸気の境界 深度2',
    steam_boundary_depth_3: '蒸気の境界 深度3',
    // 黄昏の境界（光・闇）
    twilight_realm_depth_1: '黄昏の境界 深度1',
    twilight_realm_depth_2: '黄昏の境界 深度2',
    twilight_realm_depth_3: '黄昏の境界 深度3',
    // 時空の狭間（未来）
    time_rift_depth_1: '時空の狭間 深度1',
    time_rift_depth_2: '時空の狭間 深度2',
    time_rift_depth_3: '時空の狭間 深度3',
    // 原始の深淵（原始）
    primal_depths_depth_1: '原始の深淵 深度1',
    primal_depths_depth_2: '原始の深淵 深度2',
    primal_depths_depth_3: '原始の深淵 深度3',
    // 未知の領域（なし）
    unknown_depth_1: '未知の領域 深度1',
    unknown_depth_2: '未知の領域 深度2',
    unknown_depth_3: '未知の領域 深度3',
    // デフォルト
    default: '全域',
  };
  return locationMap[locationKey] || locationKey;
}

/**
 * 宝箱タイプを日本語に変換
 */
function formatChestType(chestType) {
  const chestMap = {
    common: '通常宝箱',
    rare: 'レア宝箱',
    epic: 'エピック宝箱',
    legendary: '伝説宝箱',
  };
  return chestMap[chestType] || chestType;
}

/**
 * 装備タイプを日本語に変換
 */
function formatEquipmentType(type) {
  const typeMap = {
    weapon: '武器',
    armor: '防具',
    accessory: 'アクセサリ',
    special: '特殊装備',
  };
  return typeMap[type] || type;
}

// ========================================
// ティアカラー
// ========================================

const TIER_COLORS = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

// ティア日本語名マッピング
const TIER_NAMES = {
  common: 'コモン',
  uncommon: 'アンコモン',
  rare: 'レア',
  epic: 'エピック',
  legendary: 'レジェンダリー',
};

// ========================================
// コンポーネント
// ========================================

const MaterialDetailScreen = ({ materialId }) => {
  const { goBack, navigateToDetail } = useCardDex();
  const { playerData, allCards } = useCardData();
  const isMobile = useIsMobile();

  const material = getMaterialById(materialId);
  const ownedCount = playerData?.twinKeyData?.materials?.[materialId] || 0;

  // 入手方法の逆引き
  const obtainMethods = useMemo(
    () => getObtainMethods(material?.name),
    [material?.name]
  );

  // 用途の逆引き
  const usages = useMemo(() => getUsages(material?.name), [material?.name]);

  // 入手方法の総数
  const totalObtainMethods = useMemo(() => {
    return (
      obtainMethods.floors.length +
      obtainMethods.categories.length +
      obtainMethods.bosses.length +
      obtainMethods.cards.length +
      obtainMethods.chests.length
    );
  }, [obtainMethods]);

  // スタイル
  const styles = {
    container: {
      padding: isMobile ? '8px' : '16px',
    },
    backButton: {
      ...navStyles.backButton,
      padding: isMobile ? '8px 14px' : '10px 20px',
      fontSize: isMobile ? '12px' : '14px',
      marginBottom: '16px',
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px',
    },
    icon: {
      fontSize: isMobile ? '48px' : '64px',
      marginBottom: '12px',
    },
    name: {
      fontSize: isMobile ? '22px' : '28px',
      fontWeight: 'bold',
      color: COLORS.textGold,
      marginBottom: '8px',
    },
    tierBadge: {
      display: 'inline-block',
      fontSize: '12px',
      padding: '4px 12px',
      borderRadius: '6px',
      fontWeight: 'bold',
      marginBottom: '12px',
    },
    description: {
      fontSize: '14px',
      color: COLORS.textSecondary,
      lineHeight: '1.6',
      marginBottom: '12px',
    },
    ownedCount: {
      fontSize: '16px',
      color: COLORS.textPrimary,
    },
    section: {
      background: 'rgba(20, 20, 40, 0.6)',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      border: `1px solid ${COLORS.borderSecondary}`,
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: 'bold',
      color: COLORS.textPrimary,
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    subSection: {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '8px',
    },
    subSectionTitle: {
      fontSize: '13px',
      fontWeight: 'bold',
      color: COLORS.textSecondary,
      marginBottom: '8px',
    },
    dropItem: {
      fontSize: '12px',
      color: COLORS.textPrimary,
      marginBottom: '4px',
      paddingLeft: '12px',
    },
    usageItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '6px',
      marginBottom: '6px',
    },
    usageName: {
      fontSize: '13px',
      color: COLORS.textPrimary,
    },
    usageType: {
      fontSize: '11px',
      color: COLORS.textSecondary,
    },
    usageCount: {
      fontSize: '12px',
      color: COLORS.textGold,
    },
    emptyMessage: {
      fontSize: '13px',
      color: COLORS.textSecondary,
      fontStyle: 'italic',
    },
    cardLink: {
      color: COLORS.accent,
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  };

  if (!material) {
    return (
      <div style={styles.container}>
        <button onClick={goBack} style={styles.backButton}>
          ◀ 戻る
        </button>
        <div style={{ textAlign: 'center', color: COLORS.textSecondary }}>
          素材が見つかりません (ID: {materialId})
        </div>
      </div>
    );
  }

  const tierColor = TIER_COLORS[material.tier] || TIER_COLORS.common;

  // カード名を取得
  const getCardName = (cardId) => {
    const card = allCards.find((c) => c.id === cardId);
    return card?.name || cardId;
  };

  return (
    <div style={styles.container}>
      {/* 戻るボタン */}
      <button onClick={goBack} style={styles.backButton}>
        ◀ 戻る
      </button>

      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.icon}>{material.icon || '📦'}</div>
        <div style={styles.name}>{material.name}</div>
        <div
          style={{
            ...styles.tierBadge,
            background: `${tierColor}33`,
            color: tierColor,
            border: `1px solid ${tierColor}`,
          }}
        >
          {TIER_NAMES[material.tier] || material.tier}
        </div>
        <div style={styles.description}>{material.description}</div>
        <div style={styles.ownedCount}>所持数: {ownedCount}</div>
      </div>

      {/* 入手方法セクション */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          <span>📍</span>
          <span>入手方法</span>
          <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
            ({totalObtainMethods}件)
          </span>
        </div>

        {totalObtainMethods === 0 ? (
          <div style={styles.emptyMessage}>入手方法の情報がありません</div>
        ) : (
          <>
            {/* 大陸ドロップ */}
            {obtainMethods.floors.length > 0 && (
              <div style={styles.subSection}>
                <div style={styles.subSectionTitle}>🗺️ 大陸ドロップ</div>
                {obtainMethods.floors.map((drop, idx) => (
                  <div key={idx} style={styles.dropItem}>
                    ・{formatLocation(drop.location)}:{' '}
                    {drop.type === 'rare'
                      ? `${(drop.chance * 100).toFixed(0)}% (レア)`
                      : `${(drop.chance * 100).toFixed(0)}%`}
                    {drop.countRange && ` (${drop.countRange[0]}-${drop.countRange[1]}個)`}
                  </div>
                ))}
              </div>
            )}

            {/* カテゴリドロップ */}
            {obtainMethods.categories.length > 0 && (
              <div style={styles.subSection}>
                <div style={styles.subSectionTitle}>👹 カテゴリドロップ</div>
                {obtainMethods.categories.map((drop, idx) => (
                  <div key={idx} style={styles.dropItem}>
                    ・{drop.category}系モンスター:{' '}
                    {drop.type === 'rare'
                      ? `${(drop.chance * 100).toFixed(0)}% (レア)`
                      : `${(drop.chance * 100).toFixed(0)}%`}
                    {drop.countRange && ` (${drop.countRange[0]}-${drop.countRange[1]}個)`}
                  </div>
                ))}
              </div>
            )}

            {/* ボスドロップ */}
            {obtainMethods.bosses.length > 0 && (
              <div style={styles.subSection}>
                <div style={styles.subSectionTitle}>💀 ボスドロップ</div>
                {obtainMethods.bosses.map((drop, idx) => (
                  <div key={idx} style={styles.dropItem}>
                    ・{drop.bossId}:{' '}
                    {drop.type === 'guaranteed'
                      ? `確定${drop.count}個`
                      : drop.type === 'firstClear'
                      ? `初回クリア報酬${drop.count}個`
                      : `${(drop.chance * 100).toFixed(0)}% ${drop.count}個`}
                  </div>
                ))}
              </div>
            )}

            {/* カード固有ドロップ */}
            {obtainMethods.cards.length > 0 && (
              <div style={styles.subSection}>
                <div style={styles.subSectionTitle}>🎴 カード固有ドロップ</div>
                {obtainMethods.cards.map((drop, idx) => (
                  <div key={idx} style={styles.dropItem}>
                    ・
                    <span
                      style={styles.cardLink}
                      onClick={() => navigateToDetail(drop.cardId)}
                    >
                      {getCardName(drop.cardId)}
                    </span>
                    :{' '}
                    {drop.type === 'guaranteed'
                      ? `確定${drop.count}個`
                      : drop.type === 'rare'
                      ? 'レアドロップ'
                      : `${(drop.chance * 100).toFixed(0)}% (${drop.countRange[0]}-${drop.countRange[1]}個)`}
                  </div>
                ))}
              </div>
            )}

            {/* 宝箱ドロップ */}
            {obtainMethods.chests.length > 0 && (
              <div style={styles.subSection}>
                <div style={styles.subSectionTitle}>🎁 宝箱ドロップ</div>
                {obtainMethods.chests.map((drop, idx) => (
                  <div key={idx} style={styles.dropItem}>
                    ・{formatChestType(drop.chestType)}
                    {drop.countRange && ` (${drop.countRange[0]}-${drop.countRange[1]}個)`}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 用途セクション */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          <span>🔧</span>
          <span>用途（使用レシピ）</span>
          <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
            ({usages.length}件)
          </span>
        </div>

        {usages.length === 0 ? (
          <div style={styles.emptyMessage}>使用するレシピがありません</div>
        ) : (
          usages.map((usage, idx) => {
            const usageTierColor = TIER_COLORS[usage.recipeTier] || TIER_COLORS.common;
            return (
              <div key={idx} style={styles.usageItem}>
                <div>
                  <div style={styles.usageName}>{usage.recipeName}</div>
                  <div style={styles.usageType}>
                    {formatEquipmentType(usage.recipeType)} /{' '}
                    <span style={{ color: usageTierColor }}>
                      {TIER_NAMES[usage.recipeTier] || usage.recipeTier}
                    </span>
                  </div>
                </div>
                <div style={styles.usageCount}>×{usage.requiredCount}</div>
              </div>
            );
          })
        )}
      </div>

      {/* 追加情報（属性・カテゴリ固有） */}
      {(material.attribute || material.category) && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span>ℹ️</span>
            <span>追加情報</span>
          </div>
          {material.attribute && (
            <div style={{ fontSize: '13px', color: COLORS.textPrimary, marginBottom: '4px' }}>
              属性: {material.attribute}
            </div>
          )}
          {material.category && (
            <div style={{ fontSize: '13px', color: COLORS.textPrimary }}>
              カテゴリ: {material.category}
            </div>
          )}
          {material.expValue && (
            <div style={{ fontSize: '13px', color: COLORS.textPrimary, marginTop: '4px' }}>
              経験値: {material.expValue} EXP
            </div>
          )}
          {material.rarity && (
            <div style={{ fontSize: '13px', color: COLORS.textPrimary, marginTop: '4px' }}>
              対応レアリティ: {material.rarity}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MaterialDetailScreen;
